import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Sparkles, Image, FileText, Layers, Download, Copy, 
  Check, X, ThumbsUp, ThumbsDown, RefreshCw, Clock,
  Settings, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const PostaJaPainel = () => {
  const { slug } = useParams();
  const [cliente, setCliente] = useState(null);
  const [conteudos, setConteudos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [filtro, setFiltro] = useState('todos');
  const [conteudoExpandido, setConteudoExpandido] = useState(null);

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    try {
      const [cliRes, cntRes, statsRes] = await Promise.all([
        fetch(`${API}/api/postaja/clientes/${slug}`),
        fetch(`${API}/api/postaja/clientes/${slug}/conteudos`),
        fetch(`${API}/api/postaja/clientes/${slug}/dashboard`)
      ]);

      const [cliData, cntData, statsData] = await Promise.all([
        cliRes.json(),
        cntRes.json(),
        statsRes.json()
      ]);

      setCliente(cliData);
      setConteudos(cntData);
      setStats(statsData);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const gerarConteudo = async (tipo) => {
    setGerando(true);
    try {
      const res = await fetch(`${API}/api/postaja/clientes/${slug}/gerar-conteudo?tipo=${tipo}`, {
        method: 'POST'
      });
      if (res.ok) {
        loadData();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setGerando(false);
    }
  };

  const atualizarStatus = async (conteudoId, status) => {
    try {
      await fetch(`${API}/api/postaja/clientes/${slug}/conteudos/${conteudoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const copiarTexto = (texto) => {
    navigator.clipboard.writeText(texto);
    alert('Texto copiado!');
  };

  const baixarImagem = (url, nome) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = nome || 'conteudo.png';
    link.click();
  };

  const conteudosFiltrados = conteudos.filter(c => {
    if (filtro === 'todos') return true;
    if (filtro === 'pendentes') return c.status === 'pendente';
    if (filtro === 'aprovados') return c.status === 'aprovado';
    return c.tipo === filtro;
  });

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'story': return <Image size={16} className="text-pink-500" />;
      case 'post': return <FileText size={16} className="text-violet-500" />;
      case 'carrossel': return <Layers size={16} className="text-fuchsia-500" />;
      default: return <Sparkles size={16} />;
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'story': return 'Story';
      case 'post': return 'Post';
      case 'carrossel': return 'Carrossel';
      default: return tipo;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="text-violet-500" size={20} />
              {cliente?.nome_negocio || 'PostaJá'}
            </h1>
            <p className="text-sm text-slate-500">
              Plano {cliente?.plano === 'plus' ? 'Plus' : 'Básico'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => gerarConteudo('story')}
              disabled={gerando}
            >
              {gerando ? <RefreshCw className="animate-spin" size={16} /> : <Image size={16} />}
              Gerar Story
            </Button>
            <Button 
              size="sm"
              className="bg-violet-600 hover:bg-violet-700"
              onClick={() => gerarConteudo('post')}
              disabled={gerando}
            >
              {gerando ? <RefreshCw className="animate-spin" size={16} /> : <FileText size={16} />}
              Gerar Post
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 border">
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
            <div className="bg-white rounded-xl p-4 border">
              <p className="text-2xl font-bold text-amber-500">{stats.pendentes}</p>
              <p className="text-xs text-slate-500">Pendentes</p>
            </div>
            <div className="bg-white rounded-xl p-4 border">
              <p className="text-2xl font-bold text-green-500">{stats.aprovados}</p>
              <p className="text-xs text-slate-500">Aprovados</p>
            </div>
            <div className="bg-white rounded-xl p-4 border">
              <p className="text-2xl font-bold text-pink-500">{stats.por_tipo?.stories || 0}</p>
              <p className="text-xs text-slate-500">Stories</p>
            </div>
            <div className="bg-white rounded-xl p-4 border">
              <p className="text-2xl font-bold text-violet-500">{stats.por_tipo?.posts || 0}</p>
              <p className="text-xs text-slate-500">Posts</p>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['todos', 'pendentes', 'aprovados', 'story', 'post', 'carrossel'].map(f => (
            <button
              key={f}
              onClick={() => setFiltro(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                filtro === f 
                  ? 'bg-violet-600 text-white' 
                  : 'bg-white text-slate-600 border hover:border-violet-300'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Content Grid */}
        {conteudosFiltrados.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border">
            <Sparkles size={48} className="text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Nenhum conteúdo ainda</h3>
            <p className="text-slate-500 mb-6">Clique em "Gerar Story" ou "Gerar Post" para criar seu primeiro conteúdo.</p>
            <Button 
              onClick={() => gerarConteudo('story')} 
              className="bg-violet-600 hover:bg-violet-700"
              disabled={gerando}
            >
              {gerando ? 'Gerando...' : 'Gerar meu primeiro Story'}
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {conteudosFiltrados.map((conteudo) => (
              <div 
                key={conteudo.conteudo_id} 
                className="bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`conteudo-${conteudo.conteudo_id}`}
              >
                {/* Image */}
                {conteudo.imagem_url && (
                  <div 
                    className="aspect-square bg-slate-100 relative cursor-pointer"
                    onClick={() => setConteudoExpandido(conteudo)}
                  >
                    <img 
                      src={conteudo.imagem_url} 
                      alt={conteudo.tipo}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 flex items-center gap-1 bg-white/90 px-2 py-1 rounded-full text-xs font-medium">
                      {getTipoIcon(conteudo.tipo)}
                      {getTipoLabel(conteudo.tipo)}
                    </div>
                    <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                      conteudo.status === 'aprovado' ? 'bg-green-100 text-green-700' :
                      conteudo.status === 'descartado' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {conteudo.status}
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="p-4">
                  <p className="text-sm text-slate-600 line-clamp-3 mb-3">
                    {conteudo.texto}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {formatDate(conteudo.gerado_em)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {conteudo.status === 'pendente' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                          onClick={() => atualizarStatus(conteudo.conteudo_id, 'aprovado')}
                        >
                          <ThumbsUp size={14} />
                          Aprovar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => atualizarStatus(conteudo.conteudo_id, 'descartado')}
                        >
                          <ThumbsDown size={14} />
                          Descartar
                        </Button>
                      </>
                    )}
                    {conteudo.status === 'aprovado' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => copiarTexto(conteudo.texto)}
                        >
                          <Copy size={14} />
                          Copiar
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 bg-violet-600 hover:bg-violet-700"
                          onClick={() => baixarImagem(conteudo.imagem_url, `${conteudo.tipo}-${conteudo.conteudo_id}.png`)}
                        >
                          <Download size={14} />
                          Baixar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Modal Expandido */}
      {conteudoExpandido && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setConteudoExpandido(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {conteudoExpandido.imagem_url && (
              <img 
                src={conteudoExpandido.imagem_url} 
                alt={conteudoExpandido.tipo}
                className="w-full"
              />
            )}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4">
                {getTipoIcon(conteudoExpandido.tipo)}
                <span className="font-medium">{getTipoLabel(conteudoExpandido.tipo)}</span>
                <span className={`ml-auto px-2 py-1 rounded-full text-xs ${
                  conteudoExpandido.status === 'aprovado' ? 'bg-green-100 text-green-700' :
                  conteudoExpandido.status === 'descartado' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {conteudoExpandido.status}
                </span>
              </div>
              
              <p className="text-slate-700 whitespace-pre-wrap mb-6">
                {conteudoExpandido.texto}
              </p>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => copiarTexto(conteudoExpandido.texto)}
                >
                  <Copy size={16} />
                  Copiar texto
                </Button>
                <Button
                  className="flex-1 bg-violet-600 hover:bg-violet-700"
                  onClick={() => baixarImagem(conteudoExpandido.imagem_url, `${conteudoExpandido.tipo}.png`)}
                >
                  <Download size={16} />
                  Baixar imagem
                </Button>
              </div>
            </div>
            
            <button 
              onClick={() => setConteudoExpandido(null)}
              className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostaJaPainel;
